<<template>
  <div class="enroll-shell">

    <!-- Agent ribbon -->
    <div class="agent-ribbon" v-if="agentName || agentSerial">
      <div class="ribbon-inner">
        <div class="ribbon-left">
          <span class="ribbon-label">Logged in as agent</span>
          <span class="ribbon-name">{{ agentName }}</span>
        </div>
        <div class="ribbon-right">
          <div class="serial-pill" :title="agentSerial">
            <span class="pill-prefix">AGT</span>
            <span class="pill-mask">{{ maskedSerial }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="agent-ribbon ribbon-error" v-else-if="agentError">
      <div class="ribbon-inner">
        <v-icon small color="#EF4444">mdi-alert-circle</v-icon>
        <span class="ribbon-err">{{ agentError }}</span>
      </div>
    </div>

    <div class="agent-ribbon ribbon-loading" v-else-if="agentLoading">
      <div class="ribbon-inner">
        <span class="load-dots"></span>
        <span class="ribbon-load">Verifying agent credentials…</span>
      </div>
    </div>

    <!-- Header -->
    <header class="page-header">
      <div class="header-inner">
        <a href="javascript:history.go(-1)" class="back-link">
          <v-icon small color="#0F172A">mdi-arrow-left</v-icon>
          <span>Back</span>
        </a>
        <div class="brand">
          <span class="brand-main">PettyCash</span>
          <span class="brand-sas">SAS</span>
        </div>
        <div class="header-spacer"></div>
      </div>
    </header>

    <!-- Main -->
    <main class="enroll-main">

      <!-- Left: Context -->
      <section class="context-panel">
        <div class="context-inner">
          <span class="context-tag">Agent Portal</span>
          <h1 class="context-title">
            Register a<br><strong>New Client</strong>
          </h1>
          <p class="context-body">
            You are enrolling a new user under your TopUp agent account.
            Their profile will be automatically linked to your serial number.
          </p>

          <ul class="context-list">
            <li>
              <span class="list-dot" style="background:#00479B"></span>
              <div>
                <strong>Auto-linked</strong>
                <p>Client linked to your serial instantly</p>
              </div>
            </li>
            <li>
              <span class="list-dot" style="background:#10B981"></span>
              <div>
                <strong>Verified Signup</strong>
                <p>OTP confirmation sent to client phone</p>
              </div>
            </li>
            <li>
              <span class="list-dot" style="background:#D4AF37"></span>
              <div>
              <strong>Identity Protection</strong>
              <p>Your account details are securely verified and encrypted</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <!-- Right: Form -->
      <section class="form-panel">
        <div class="form-card">

          <div class="form-head">
            <h2>Client Details</h2>
            <p>Fill in the new user's information below</p>
          </div>

          <!-- Account Type Toggle -->
          <div class="type-toggle" :class="{ 'business-active': accountType === 'business' }">
            <button
              class="type-btn"
              @click="accountType = 'basic'"
            >
              <v-icon x-small class="btn-icon" :color="accountType === 'basic' ? '#059669' : '#94A3B8'">mdi-account</v-icon>
              <span class="btn-label">Basic Account</span>
              <span class="type-badge free">Free</span>
            </button>
            <button
              class="type-btn"
              @click="accountType = 'business'"
            >
              <v-icon x-small class="btn-icon" :color="accountType === 'business' ? '#D4AF37' : '#94A3B8'">mdi-briefcase</v-icon>
              <span class="btn-label">Business Suite</span>
              <span class="type-badge paid">10,500 FCFA</span>
            </button>
          </div>

          <!-- Progress -->
          <div class="progress-line">
            <div class="progress-track">
              <div class="progress-bar" :style="{ width: formProgress + '%' }"></div>
            </div>
            <span class="progress-num">{{ Math.round(formProgress) }}%</span>
          </div>

          <!-- Fields -->
          <div class="fields">

            <!-- Full Names -->
            <div class="field" :class="{ done: fullnames.length >= 4 }">
              <label>Full Names</label>
              <div class="input-wrap">
                <v-icon small color="#64748B">mdi-account</v-icon>
                <input
                  v-model="fullnames"
                  ref="fullnames"
                  type="text"
                  placeholder="e.g. Jean Paul Mbarga"
                  @keyup.enter="$refs.email.focus()"
                />
                <transition name="fade">
                  <v-icon v-if="fullnames.length >= 4" x-small color="#10B981">mdi-check</v-icon>
                </transition>
              </div>
            </div>

            <!-- Email -->
            <div class="field" :class="{ done: isValidEmail }">
              <label>Email Address</label>
              <div class="input-wrap">
                <v-icon small color="#64748B">mdi-email</v-icon>
                <input
                  v-model="email"
                  ref="email"
                  type="email"
                  placeholder="client@example.com"
                  @keyup.enter="$refs.username.focus()"
                />
                <transition name="fade">
                  <v-icon v-if="isValidEmail" x-small color="#10B981">mdi-check</v-icon>
                </transition>
              </div>
            </div>

            <!-- Username -->
            <div class="field" :class="{ done: username.length >= 4 }">
              <label>Username</label>
              <div class="input-wrap">
                <v-icon small color="#64748B">mdi-at</v-icon>
                <input
                  v-model="username"
                  ref="username"
                  type="text"
                  placeholder="unique_username"
                  @keyup.enter="$refs.password.focus()"
                />
                <transition name="fade">
                  <v-icon v-if="username.length >= 4" x-small color="#10B981">mdi-check</v-icon>
                </transition>
              </div>
            </div>

            <!-- Password -->
            <div class="field" :class="{ done: password.length >= 8 }">
              <label>Password</label>
              <div class="input-wrap">
                <v-icon small color="#64748B">mdi-lock</v-icon>
                <input
                  v-model="password"
                  ref="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Min. 8 characters"
                  @keyup.enter="$refs.phoneInput.focus()"
                />
                <button class="eye-btn" @click.prevent="showPassword = !showPassword" tabindex="-1">
                  <v-icon x-small color="#94A3B8">{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </button>
              </div>
              <div class="strength" v-if="password.length > 0">
                <div class="strength-track">
                  <div class="strength-bar" :class="passwordStrengthClass" :style="{ width: passwordStrengthWidth }"></div>
                </div>
                <span class="strength-text" :class="passwordStrengthClass">{{ passwordStrengthLabel }}</span>
              </div>
            </div>

            <!-- Phone -->
            <div class="field" :class="{ done: phoneValid }">
              <label>Phone Number</label>
              <div class="input-wrap phone-wrap">
                <input
                  id="topup-phone"
                  ref="phoneInput"
                  v-model="phone_number"
                  type="tel"
                  placeholder="Enter phone number"
                  class="phone-input"
                  @click="clearPhoneField"
                />
                <transition name="fade">
                  <v-icon v-if="phoneValid" x-small color="#10B981">mdi-check</v-icon>
                </transition>
              </div>
            </div>

            <!-- Agent Code (visual, read-only) -->
            <div class="field agent-field">
              <label>
                Agent Code
                <span class="lock-tag"><v-icon x-small color="#B8941F">mdi-lock</v-icon> Locked</span>
              </label>
              <div class="agent-plate">
                <div class="plate-left">
                  <v-icon small color="#B8941F">mdi-identifier</v-icon>
                </div>
                <div class="plate-center">
                  <div class="plate-code" :title="agentSerial">
                    <span class="code-prefix">AGENT</span>
                    <span class="code-divider">—</span>
                    <span class="code-mask">{{ maskedSerial }}</span>
                  </div>
                  <div class="plate-hint">Fetched from your account</div>
                </div>
                <div class="plate-right">
                  <v-icon x-small color="#B8941F">mdi-shield-lock</v-icon>
                </div>
              </div>
            </div>

          </div>

          <!-- Submit -->
          <button
            class="submit-btn"
            @click="handleSubmit"
            :disabled="$store.state.isLoading || !agentValid || !isFormValid"
          >
            <span v-if="!$store.state.isLoading" class="btn-text">
              <v-icon small color="white">{{ accountType === 'business' ? 'mdi-briefcase' : 'mdi-account-plus' }}</v-icon>
              {{ accountType === 'business' ? 'Continue to Payment' : 'Register Client' }}
            </span>
            <span v-else class="btn-loading">
              <span class="spinner"></span>
              {{ accountType === 'business' ? 'Processing…' : 'Creating account…' }}
            </span>
          </button>



        </div>
      </section>
    </main>

    <!-- ═══════════════════════════════════════
         PAYMENT DIALOG (Business only)
         With improved mobile UX: sticky buttons at bottom
         ═══════════════════════════════════════ -->
    <v-dialog :max-width="480" width="100%" v-model="showPayment" content-class="payment-dialog">
      <v-card class="payment-card">
        <div class="payment-head">
          <v-icon color="white" size="28">mdi-credit-card-check</v-icon>
          <div>
            <div class="payment-title">Confirm Deposit</div>
            <div class="payment-subtitle">10,500 FCFA</div>
          </div>
        </div>

        <!-- v-card-text now contains ALL content including actions for mobile sticky behavior -->
        <v-card-text class="payment-body">
          <!-- Transaction Phone -->
          <div class="field">
            <label>Transaction Phone</label>
            <div class="input-wrap phone-wrap">
              <input
                id="transaction-phone"
                type="tel"
                placeholder="Enter phone number"
                class="phone-input"
                @click="clearTransactionPhoneField"
              />
            </div>
            <div v-if="transactionPhoneError" class="field-error">{{ transactionPhoneError }}</div>
          </div>

          <!-- Operators -->
          <div class="operators">
            <div
              @click="selectOperator('MTN')"
              :class="{ selected: paymentMethod === 'MTN' }"
              class="operator-card"
            >
              <v-img :src="require('@/assets/mtn-mobile-money-logo.png')" contain height="40"></v-img>
              <span>MTN Mobile Money</span>
              <div v-if="paymentMethod === 'MTN'" class="op-check">
                <v-icon x-small color="white">mdi-check</v-icon>
              </div>
            </div>
            <div
              @click="selectOperator('OM')"
              :class="{ selected: paymentMethod === 'OM' }"
              class="operator-card"
            >
              <v-img :src="require('@/assets/om.png')" contain height="40"></v-img>
              <span>Orange Money</span>
              <div v-if="paymentMethod === 'OM'" class="op-check">
                <v-icon x-small color="white">mdi-check</v-icon>
              </div>
            </div>
          </div>

          <!-- Summary -->
          <div class="payment-summary">
            By clicking <b>Confirm</b>, a deposit of <b>10,500 F CFA</b> will be triggered via
            <b v-if="paymentMethod">{{ paymentMethod === 'MTN' ? 'MTN Mobile Money' : 'Orange Money' }}</b>
            <b v-else class="red--text">[select method]</b>
            from <b>{{ transactionPhoneNumber || '[phone number]' }}</b>
          </div>

          <!-- USSD -->
          <transition name="expand">
            <div v-if="paymentMethod" class="ussd-block">
              <p class="ussd-hint">If you are facing difficulties, send manually:</p>
              <div class="ussd-row">
                <code class="ussd-code">{{ ussdCode }}</code>
                <button class="ussd-copy" @click="copyUSSD">
                  <v-icon x-small>mdi-content-copy</v-icon>
                </button>
              </div>
              <ul class="ussd-steps">
                <li v-if="paymentMethod === 'MTN'">Choose reference 1</li>
                <li v-if="paymentMethod === 'OM'">Input transaction amount</li>
                <li>Always <b>Confirm</b> below</li>
              </ul>
            </div>
          </transition>

          <!--
            ACTIONS: Moved inside v-card-text for sticky bottom behavior on mobile.
            On desktop they remain at the end of content (no sticky needed),
            on mobile they will stick to bottom of screen when scrolling.
          -->
          <div class="payment-actions">
            <button class="action-cancel" @click="showPayment = false">Cancel</button>
            <button
              class="action-confirm"
              @click="handlePaymentConfirmation"
              :disabled="!paymentMethod || !transactionPhoneNumber || processingPayment"
            >
              <span v-if="!processingPayment">Confirm Payment</span>
              <span v-else class="btn-loading"><span class="spinner"></span>Processing…</span>
            </button>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'savings.clients.register',

  data() {
    return {
      // Agent
      agentSerial: '',
      agentName: '',
      agentLoading: false,
      agentError: '',
      agentValid: false,

      // Account type
      accountType: 'basic',

      // Form
      fullnames: '',
      email: '',
      username: '',
      password: '',
      phone_number: 'Enter Phone Number',
      showPassword: false,
      phoneInput: null,
      phoneValid: false,

      // Payment (business)
      showPayment: false,
      paymentMethod: '',
      transactionPhoneNumber: '',
      transactionPhoneInput: null,
      transactionPhoneError: '',
      transactionId: '',
      ussdCode: '',
      hrefTel: '',
      processingPayment: false,
    }
  },

  computed: {
    isValidEmail() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)
    },

    passwordStrengthWidth() {
      const l = this.password.length
      if (l === 0) return '0%'
      if (l < 6) return '25%'
      if (l < 8) return '50%'
      if (l < 12) return '75%'
      return '100%'
    },

    passwordStrengthLabel() {
      const l = this.password.length
      if (l < 6) return 'Weak'
      if (l < 8) return 'Fair'
      if (l < 12) return 'Good'
      return 'Strong'
    },

    passwordStrengthClass() {
      return {
        'pw-weak': this.password.length < 6,
        'pw-fair': this.password.length >= 6 && this.password.length < 8,
        'pw-good': this.password.length >= 8 && this.password.length < 12,
        'pw-strong': this.password.length >= 12,
      }
    },

    formProgress() {
      let score = 0
      if (this.fullnames.length >= 4) score++
      if (this.isValidEmail) score++
      if (this.username.length >= 4) score++
      if (this.password.length >= 8) score++
      if (this.phoneValid) score++
      return (score / 5) * 100
    },

    isFormValid() {
      return this.fullnames.length >= 4 &&
             this.isValidEmail &&
             this.username.length >= 4 &&
             this.password.length >= 8 &&
             this.phoneValid
    },

    maskedSerial() {
      const raw = this.agentSerial
      if (!raw) return '···· ····'
      const m = raw.match(/^([A-Za-z]+)(\d+)$/)
      if (!m) return raw
      const prefix = m[1]
      const nums = m[2]
      const len = nums.length
      if (len <= 4) {
        return prefix + ' ·' + '·'.repeat(len - 2) + nums.slice(-2)
      }
      const visible = nums.slice(-3)
      const maskedCount = Math.max(4, len - 3)
      return prefix + ' ' + '·'.repeat(maskedCount) + visible
    },
  },

  async mounted() {
    document.title = 'PettyCash | TopUp Enrollment'
    await this.loadAgentInfo()
    this.$nextTick(() => this.initPhone())
  },

  watch: {
    showPayment(newVal) {
      if (newVal) {
        this.paymentMethod = ''
        this.ussdCode = ''
        this.transactionId = ''
        this.transactionPhoneNumber = ''
        this.transactionPhoneError = ''
        this.$nextTick(() => this.initTransactionPhone())
      }
    },
  },

  methods: {
    async loadAgentInfo() {
      this.agentLoading = true
      this.agentError = ''
      try {
        const res = await axios.get('/api/v1/topup/my-agent-info/')
        if (res.data && res.data.serial_no) {
          this.agentSerial = res.data.serial_no
          this.agentName = res.data.full_name || res.data.username
          this.agentValid = true
        } else {
          this.agentError = "Vous n'êtes pas autorisé à enregistrer des clients TopUp."
        }
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.agentError = 'Accès refusé : vous devez être un agent TopUp.'
        } else {
          this.agentError = 'Impossible de récupérer vos informations. Vérifiez votre connexion.'
        }
        console.error(error)
      } finally {
        this.agentLoading = false
      }
    },

    initPhone() {
      const phoneField = document.querySelector('#topup-phone')
      if (!phoneField || !window.intlTelInput) return
      this.phoneInput = window.intlTelInput(phoneField, {
        initialCountry: 'cm',
        geoIpLookup(callback) {
          fetch('https://ipinfo.io/json?token=fa5e74d3e65812', { headers: { Accept: 'application/json' } })
            .then(r => r.json())
            .catch(() => ({ country: 'cm' }))
            .then(r => callback(r.country))
        },
        preferredCountries: ['cm', 'ci', 'cg', 'ng', 'ga', 'td', 'cf', 'gq'],
        utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
      })
      phoneField.addEventListener('input', () => {
        this.phoneValid = this.phoneInput && this.phoneInput.isValidNumber()
      })
      phoneField.addEventListener('countrychange', () => {
        this.phoneValid = this.phoneInput && this.phoneInput.isValidNumber()
      })
    },

    clearPhoneField() {
      if (this.phone_number === 'Enter Phone Number') this.phone_number = ''
    },

    initTransactionPhone() {
      const field = document.querySelector('#transaction-phone')
      if (!field || !window.intlTelInput) return
      this.transactionPhoneInput = window.intlTelInput(field, {
        initialCountry: 'cm',
        geoIpLookup(callback) {
          fetch('https://ipinfo.io/json?token=fa5e74d3e65812', { headers: { Accept: 'application/json' } })
            .then(r => r.json())
            .catch(() => ({ country: 'cm' }))
            .then(r => callback(r.country))
        },
        preferredCountries: ['cm', 'ci', 'cg', 'ng', 'ga', 'td', 'cf', 'gq'],
        utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
      })
      field.addEventListener('input', () => {
        this.transactionPhoneNumber = this.transactionPhoneInput.getNumber()
        this.transactionPhoneError = ''
      })
    },

    clearTransactionPhoneField() {
      const current = this.transactionPhoneInput?.getNumber()
      if (!current || current === 'Enter Phone Number') {
        this.transactionPhoneInput?.setNumber('')
      }
      this.transactionPhoneError = ''
    },

    validateTransactionPhone() {
      if (!this.transactionPhoneInput || !this.transactionPhoneInput.isValidNumber()) {
        this.transactionPhoneError = 'Phone number is invalid. Please input a valid number'
        return false
      }
      this.transactionPhoneError = ''
      return true
    },

    notify(msg, color = 'red darken-4') {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },

    async handleSubmit() {
      if (!this.isFormValid) {
        if (this.fullnames.length < 4) return this.notify('Full names too short') || this.$refs.fullnames.focus()
        if (!this.isValidEmail) return this.notify('Please enter a valid email') || this.$refs.email.focus()
        if (this.username.length < 4) return this.notify('Username must be at least 4 characters') || this.$refs.username.focus()
        if (this.password.length < 8) return this.notify('Password must be at least 8 characters') || this.$refs.password.focus()
        if (!this.phoneInput || !this.phoneInput.isValidNumber()) return this.notify('Phone number is invalid')
      }

      if (this.accountType === 'business') {
        this.$store.commit('setIsLoading', true)
        try {
          const res = await axios.post('/api/v1/home/validate-credentials/', {
            username: this.username,
            phone: this.phoneInput.getNumber(),
            password: this.password
          })
          if (res.data?.error) {
            this.notify(res.data.error, 'red darken-4')
            this.$store.commit('setIsLoading', false)
            return
          }
          if (res.data?.success === false) {
            this.notify(res.data.message || 'Validation failed', 'red darken-4')
            this.$store.commit('setIsLoading', false)
            return
          }
          this.showPayment = true
          this.notify('Credentials validated! Complete payment below.', 'blue darken-2')
        } catch (error) {
          this.handleApiError(error, 'Validation failed')
        } finally {
          this.$store.commit('setIsLoading', false)
        }
      } else {
        await this.createBasicAccount()
      }
    },

    async createBasicAccount() {
      this.$store.commit('setIsLoading', true)

      try {
        const contactCheck = await axios.get(`/api/v1/savings/check/client/contact/${this.phoneInput.getNumber()}/`)
        if (contactCheck.data) {
          this.notify('This phone number is already registered. Please use another.')
          this.$store.commit('setIsLoading', false)
          return
        }

        let referralValid = true
        if (this.agentSerial && this.agentSerial.length > 0) {
          const refCheck = await axios.get(`/api/v1/savings/check/partner/referal_code/${this.agentSerial}/`)
          referralValid = refCheck.data
        }
        if (!referralValid) {
          this.notify('Agent serial is invalid. Please contact support.')
          this.$store.commit('setIsLoading', false)
          return
        }

        await axios.post('/api/v1/users/', {
          username: this.username,
          password: this.password,
        })

        const payload = {
          username: this.username,
          password: this.password,
          email: this.email,
          phone_number: this.phoneInput.getNumber(),
          fullnames: this.fullnames,
          referal_code: this.agentSerial,
        }

        const res = await axios.post('/api/v1/home/create/account/', payload)
        const result = res.data[0]

        this.notify(result.text, result.color)

        if (result.valid) {
          this.notify('Client registered successfully!', 'teal darken-2')
          setTimeout(() => this.$router.push({ name: 'savings.clients.list' }), 3000)
        }
      } catch (err) {
        if (err.response && err.response.data) {
          for (const key in err.response.data) {
            this.notify(err.response.data[key][0])
            break
          }
        } else {
          this.notify('An error occurred. Please check your connection and try again.')
        }
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },

    selectOperator(operator) {
      this.paymentMethod = operator
      this.generateUSSD()
      this.transactionId = this.generateTransactionId()
    },

    generateTransactionId() {
      return `${this.paymentMethod}-${Date.now()}-${Math.floor(Math.random() * 1000)}`
    },

    generateUSSD() {
      const amount = 10500
      if (this.paymentMethod === 'MTN') {
        this.ussdCode = `*126*4*${this.$store.state.MerchantCodeMTN}*${amount}#`
        this.hrefTel = `tel:*126*4*${this.$store.state.MerchantCodeMTN}*${amount}%23`
      } else if (this.paymentMethod === 'OM') {
        this.ussdCode = `#150*47*${this.$store.state.MerchantCodeOrange}*${amount}#`
        this.hrefTel = `tel:%23150*47*${this.$store.state.MerchantCodeOrange}*${amount}%23`
      }
    },

    copyUSSD() {
      navigator.clipboard.writeText(this.ussdCode)
      this.notify('USSD code copied!', 'teal darken-2')
    },

    async handlePaymentConfirmation() {
      if (!this.paymentMethod) {
        this.notify('Please select payment operator', 'red darken-4')
        return
      }
      if (!this.validateTransactionPhone()) {
        this.notify(this.transactionPhoneError, 'red darken-4')
        return
      }

      try {
        this.generateUSSD()
        window.location.href = this.hrefTel
        await this.confirmBusinessPayment()
        setTimeout(() => {
          if (!document.hidden) window.open(this.hrefTel, '_blank')
        }, 200)
      } catch (error) {
        this.notify(error.message, 'red darken-4')
      }
    },

    async confirmBusinessPayment() {
      this.processingPayment = true
      try {
        const payload = {
          fullnames: this.fullnames,
          username: this.username,
          password: this.password,
          phone_number: this.phoneInput.getNumber(),
          payment_method: this.paymentMethod,
          email: this.email,
          transaction_id: this.transactionId,
          transaction_phone: this.transactionPhoneInput.getNumber(),
          serial_no: this.agentSerial
        }

        const res = await axios.post('/api/v1/savings/business-account/', payload)

        if (res.data.success === false || res.data.error) {
          this.notify(res.data.error || res.data.message || 'Account creation failed.', 'red darken-4')
          return
        }

        this.notify('🎉 Business account created! Redirecting…', 'teal darken-2')

        setTimeout(() => {
          this.$router.push({ name: 'savings.clients.list' })
          this.resetForm()
          this.showPayment = false
        }, 6000)

      } catch (error) {
        this.handleApiError(error, 'Account creation failed')
      } finally {
        this.processingPayment = false
      }
    },

    resetForm() {
      this.fullnames = ''
      this.email = ''
      this.username = ''
      this.password = ''
      this.phone_number = 'Enter Phone Number'
      this.phoneValid = false
      this.accountType = 'basic'
    },

    handleApiError(error, fallbackMsg) {
      let errorMessage = fallbackMsg
      if (error.response && error.response.data) {
        const d = error.response.data
        if (d.error) errorMessage = d.error
        else if (d.message) errorMessage = d.message
        else if (d.detail) errorMessage = d.detail
        else if (d.non_field_errors) errorMessage = Array.isArray(d.non_field_errors) ? d.non_field_errors.join(', ') : d.non_field_errors
        else if (typeof d === 'object') {
          const msgs = []
          for (const f in d) {
            if (Array.isArray(d[f])) msgs.push(`${f}: ${d[f].join(', ')}`)
            else if (typeof d[f] === 'string') msgs.push(`${f}: ${d[f]}`)
          }
          if (msgs.length > 0) errorMessage = msgs.join('\n')
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your internet connection.'
      } else {
        errorMessage = error.message || fallbackMsg
      }
      this.notify(errorMessage, 'red darken-4')
    },
  },
}
</script>

<style scoped>
/* ─── Base ─── */
.enroll-shell {

  min-height: 100vh;
  background: #F0F2F5;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0F172A;
  line-height: 1.5;
}

/* ─── Agent Ribbon ─── */
.agent-ribbon {
  background: #003D82;
  color: white;
  padding: 0.55rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.ribbon-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.ribbon-left { display: flex; align-items: baseline; gap: 0.6rem; }
.ribbon-label { font-size: 0.7rem; opacity: 0.65; font-weight: 500; text-transform: uppercase; letter-spacing: 0.06em; }
.ribbon-name { font-size: 0.85rem; font-weight: 600; }

.serial-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  cursor: default;
}
.pill-prefix { color: #D4AF37; font-weight: 700; }
.pill-mask { color: rgba(255,255,255,0.85); font-weight: 500; }

.ribbon-error { background: #7F1D1D; }
.ribbon-err { font-size: 0.8rem; font-weight: 500; }
.ribbon-loading { background: #1E293B; }
.ribbon-load { font-size: 0.8rem; opacity: 0.8; }

.load-dots {
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #059669;
  position: relative;
  animation: dotBounce 1.4s ease-in-out infinite;
}
.load-dots::before, .load-dots::after {
  content: '';
  position: absolute;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #059669;
  animation: dotBounce 1.4s ease-in-out infinite;
}
.load-dots::before { left: -10px; animation-delay: -0.32s; }
.load-dots::after { left: 10px; animation-delay: -0.16s; }
@keyframes dotBounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

/* ─── Header ─── */
.page-header {
  background: #FFFFFF;
  border-bottom: 1px solid #E2E8F0;
}
.header-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0.85rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  color: #0F172A;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  transition: background 0.15s;
}
.back-link:hover { background: rgba(0,61,130,0.06); }
.brand { font-size: 1.1rem; font-weight: 700; letter-spacing: -0.02em; }
.brand-main { color: #003D82; }
.brand-sas { font-size: 0.55em; color: #B8941F; font-weight: 600; vertical-align: super; margin-left: 0.1em; }
.header-spacer { width: 70px; }

/* ─── Main Layout ─── */
.enroll-main {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  padding: 2.5rem 1.25rem;
  align-items: start;
}

@media (max-width: 900px) {
  .enroll-main {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 1.5rem 1rem;
  }
  .context-panel { display: none; }
}

/* ─── Context Panel ─── */
.context-panel { padding: 1rem 0; }
.context-inner { max-width: 380px; }
.context-tag {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #B8941F;
  background: rgba(184,148,31,0.08);
  border: 1px solid rgba(184,148,31,0.2);
  padding: 0.25rem 0.7rem;
  border-radius: 3px;
  margin-bottom: 1.25rem;
}
.context-title {
  font-size: clamp(2rem, 3.5vw, 2.8rem);
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: -0.03em;
  color: #0F172A;
  margin-bottom: 1rem;
}
.context-title strong { font-weight: 700; color: #003D82; }
.context-body {
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.7;
  margin-bottom: 2rem;
}

.context-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 1rem; }
.context-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  transition: box-shadow 0.2s, border-color 0.2s;
}
.context-list li:hover { border-color: #003D82; box-shadow: 0 2px 8px rgba(0,61,130,0.06); }
.list-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-top: 0.35rem;
  flex-shrink: 0;
}
.context-list li strong { font-size: 0.85rem; font-weight: 600; color: #0F172A; display: block; margin-bottom: 0.15rem; }
.context-list li p { font-size: 0.8rem; color: #475569; margin: 0; line-height: 1.4; }

/* ─── Form Panel ─── */
.form-panel { display: flex; justify-content: center; }
.form-card {
  width: 100%;
  max-width: 440px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 2rem 1.6rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.03);
}

.form-head { margin-bottom: 1.25rem; }
.form-head h2 { font-size: 1.3rem; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 0.2rem; }
.form-head p { font-size: 0.875rem; color: #475569; margin: 0; }

/* ─── Premium Toggle Switch ─── */
.type-toggle {
  position: relative;
  display: flex;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  padding: 0.35rem;
  margin-bottom: 1.5rem;
  gap: 0.25rem;
}

.type-toggle::before {
  content: '';
  position: absolute;
  top: 0.35rem;
  bottom: 0.35rem;
  width: calc(50% - 0.35rem);
  border-radius: 8px;
  background: #FFFFFF;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04);
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), background 0.3s;
  z-index: 0;
}

.type-toggle.business-active::before {
  transform: translateX(100%);
  background: linear-gradient(135deg, #1e3a5f 0%, #00479B 100%);
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.25);
}

.type-btn {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.65rem 0.5rem;
  border-radius: 8px;
  border: none;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: color 0.3s ease;
  font-family: inherit;
  white-space: nowrap;
}

.type-btn .btn-icon {
  transition: transform 0.3s ease, color 0.3s ease;
  font-size: 0.9rem;
}

.type-btn .btn-label {
  transition: color 0.3s ease;
}

.type-btn .type-badge {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  transition: all 0.3s ease;
  margin-left: 0.2rem;
}

/* Basic active state */
.type-toggle:not(.business-active) .type-btn:first-child {
  color: #0F172A;
}
.type-toggle:not(.business-active) .type-btn:first-child .btn-icon {
  color: #059669;
  transform: scale(1.1);
}
.type-toggle:not(.business-active) .type-btn:first-child .type-badge {
  background: rgba(5, 150, 105, 0.12);
  color: #059669;
}

/* Business active state */
.type-toggle.business-active .type-btn:last-child {
  color: #FFFFFF;
}
.type-toggle.business-active .type-btn:last-child .btn-icon {
  color: #D4AF37;
  transform: scale(1.1);
}
.type-toggle.business-active .type-btn:last-child .type-badge {
  background: rgba(212, 175, 55, 0.2);
  color: #D4AF37;
}

/* Inactive hover */
.type-btn:hover:not(.active) {
  color: #003D82;
}
.type-toggle.business-active .type-btn:first-child:hover {
  color: #003D82;
}

/* Progress */
.progress-line {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.progress-track {
  flex: 1;
  height: 3px;
  background: #E2E8F0;
  border-radius: 2px;
  overflow: hidden;
}
.progress-bar {
  height: 100%;
  background: #003D82;
  border-radius: 2px;
  transition: width 0.5s ease;
}
.progress-num { font-size: 0.7rem; font-weight: 600; color: #475569; font-variant-numeric: tabular-nums; }

/* Fields */
.fields { display: flex; flex-direction: column; gap: 1rem; }
.field { transition: opacity 0.2s; }
.field.done label { color: #059669; }
.field label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.3rem;
}

.input-wrap {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.input-wrap:focus-within {
  border-color: #003D82;
  box-shadow: 0 0 0 3px rgba(0,61,130,0.06);
  background: #FFFFFF;
}
.field.done .input-wrap {
  border-color: rgba(5,150,105,0.3);
  background: rgba(5,150,105,0.02);
}
.input-wrap > .v-icon:first-child {
  width: 40px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  opacity: 0.55;
}
.input-wrap input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 500;
  color: #0F172A;
  padding: 0.65rem 0.5rem 0.65rem 0;
  min-height: 44px;
  font-family: inherit;
}
.input-wrap input::placeholder { color: #94A3B8; font-weight: 400; }
.input-wrap > .v-icon:last-child,
.input-wrap .eye-btn {
  width: 36px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
}
.eye-btn { opacity: 0.5; transition: opacity 0.15s; }
.eye-btn:hover { opacity: 0.9; }

/* Phone */
.phone-wrap { padding: 0; }
.phone-wrap .phone-input { padding-left: 50px; }

/* Strength */
.strength { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.35rem; }
.strength-track { flex: 1; height: 2px; background: #E2E8F0; border-radius: 1px; overflow: hidden; }
.strength-bar { height: 100%; border-radius: 1px; transition: width 0.3s ease, background 0.3s; }
.strength-text { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.pw-weak  { background: #DC2626; color: #DC2626; }
.pw-fair  { background: #EA580C; color: #EA580C; }
.pw-good  { background: #D97706; color: #D97706; }
.pw-strong { background: #059669; color: #059669; }

/* Agent Plate */
.agent-field { opacity: 0.9; }
.lock-tag {
  display: inline-flex; align-items: center; gap: 0.2rem;
  font-size: 0.6rem; color: #B8941F; font-weight: 700;
  background: rgba(184,148,31,0.06); border: 1px solid rgba(184,148,31,0.2);
  padding: 0.1rem 0.35rem; border-radius: 3px;
  text-transform: none; letter-spacing: 0.02em;
}
.agent-plate {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(180deg, rgba(184,148,31,0.04), rgba(184,148,31,0.02));
  border: 1.5px solid rgba(184,148,31,0.25);
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
}
.plate-left {
  width: 32px; height: 32px;
  border-radius: 6px;
  background: rgba(184,148,31,0.08);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.plate-center { flex: 1; }
.plate-code {
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #0F172A;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
.code-prefix { color: #B8941F; font-weight: 700; }
.code-divider { color: #94A3B8; font-weight: 400; }
.code-mask { color: #0F172A; font-weight: 600; }
.plate-hint { font-size: 0.7rem; color: #475569; margin-top: 0.1rem; opacity: 0.7; }
.plate-right {
  width: 28px; height: 28px;
  border-radius: 6px;
  background: rgba(184,148,31,0.06);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

/* Submit */
.submit-btn {
  width: 100%;
  margin-top: 1.5rem;
  padding: 0.8rem;
  border-radius: 8px;
  background: #003D82;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: background 0.2s, transform 0.15s;
  letter-spacing: 0.01em;
}
.submit-btn:hover:not(:disabled) { background: #0055B8; transform: translateY(-1px); }
.submit-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-text { display: flex; align-items: center; gap: 0.4rem; }
.btn-loading { display: flex; align-items: center; gap: 0.5rem; }
.spinner {
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.form-foot {
  text-align: center;
  margin-top: 1.25rem;
  font-size: 0.85rem;
  color: #475569;
}
.foot-link {
  color: #003D82;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s;
}
.foot-link:hover { border-bottom-color: #003D82; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: scale(0.85); }

/* ═══════════════════════════════════════
   PAYMENT DIALOG - IMPROVED MOBILE UX
   ═══════════════════════════════════════ */
.payment-dialog {
  border-radius: 12px !important;
  overflow: hidden;
  margin: 1rem !important;
}
@media (max-width: 520px) {
  .payment-dialog {
    margin: 0 !important;
    border-radius: 0 !important;
    max-width: 100vw !important;
    width: 100vw !important;
    height: 100vh !important;
  }
}

.payment-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px !important;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: auto;
}
@media (max-width: 520px) {
  .payment-card {
    border-radius: 0 !important;
    height: 100vh;
    display: flex;
    flex-direction: column;
  }
}

.payment-head {
  background: #003D82;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: white;
  flex-shrink: 0;
}
.payment-title { font-size: 1.1rem; font-weight: 700; }
.payment-subtitle { font-size: 0.85rem; opacity: 0.85; font-weight: 600; }

/* Payment body - becomes scrollable with sticky actions inside */
.payment-body {
  padding: 1.25rem;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
@media (max-width: 520px) {
  .payment-body {
    padding: 1rem;
    /* Ensure proper scrolling behavior for sticky elements */
    scroll-behavior: smooth;
  }
}

.payment-body .field { margin-bottom: 1rem; }
.payment-body .field label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.3rem;
  display: block;
}
.payment-body .input-wrap {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border: 1.5px solid #E2E8F0;
  border-radius: 8px;
  overflow: hidden;
}
.payment-body .input-wrap input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 500;
  color: #0F172A;
  padding: 0.65rem 0.65rem 0.65rem 3.2rem;
  min-height: 44px;
  font-family: inherit;
}
.payment-body .input-wrap .iti {
  width: 100%;
}
.payment-body .input-wrap .iti__flag-container {
  z-index: 2;
}
.payment-body .input-wrap input::placeholder { color: #94A3B8; }
.field-error {
  font-size: 0.75rem;
  color: #DC2626;
  margin-top: 0.25rem;
  font-weight: 500;
}

/* Operators */
.operators {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin: 1rem 0;
  width: 100%;
  box-sizing: border-box;
}
.operator-card {
  background: #F8FAFC;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
  padding: 0.75rem 0.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 90px;
  overflow: hidden;
  box-sizing: border-box;
}
.operator-card:hover { border-color: #003D82; }
.operator-card.selected {
  border-color: #003D82;
  background: rgba(0,61,130,0.04);
}
.operator-card .v-image,
.operator-card .v-img {
  max-width: 100% !important;
  width: auto !important;
  height: 40px !important;
  object-fit: contain !important;
}
.operator-card span {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  margin-top: 0.4rem;
  line-height: 1.2;
  word-break: break-word;
  max-width: 100%;
}
.op-check {
  position: absolute;
  top: 6px; right: 6px;
  width: 20px; height: 20px;
  border-radius: 50%;
  background: #003D82;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

/* Summary */
.payment-summary {
  background: #F8FAFC;
  border-radius: 6px;
  padding: 0.75rem;
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 1rem;
  word-break: break-word;
}
.payment-summary b { color: #0F172A; font-weight: 600; }

/* USSD */
.ussd-block { margin-top: 0.5rem; margin-bottom: 0; }
.ussd-hint { font-size: 0.8rem; color: #475569; margin-bottom: 0.5rem; }
.ussd-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #0F172A;
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 0.5rem;
  overflow-x: auto;
}
.ussd-code {
  flex: 1;
  font-family: 'SF Mono', monospace;
  font-size: 0.9rem;
  color: #059669;
  background: transparent;
  border: none;
  outline: none;
  white-space: nowrap;
}
.ussd-copy {
  background: none;
  border: none;
  color: #94A3B8;
  cursor: pointer;
  padding: 0.2rem;
  transition: color 0.2s;
  flex-shrink: 0;
}
.ussd-copy:hover { color: #FFFFFF; }
.ussd-steps {
  list-style: none;
  padding: 0;
  margin: 0 0 0.25rem 0;
}
.ussd-steps li {
  font-size: 0.8rem;
  color: #475569;
  padding: 0.25rem 0;
  padding-left: 1rem;
  position: relative;
}
.ussd-steps li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #003D82;
  font-weight: 700;
}

/* PAYMENT ACTIONS - Sticky on Mobile */
.payment-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  background: transparent;
  gap: 0.75rem;
  flex-shrink: 0;
  margin-top: 1rem;
}
/* On mobile, make actions sticky to bottom of the scrollable container */
@media (max-width: 520px) {
  .payment-actions {
    position: sticky;
    bottom: 0;
    background: #FFFFFF;
    padding: 0.75rem 0 0.5rem 0;
    margin-top: 0.5rem;
    border-top: 1px solid #E2E8F0;
    z-index: 10;
    /* Add safe area inset for modern mobiles */
    padding-bottom: env(safe-area-inset-bottom, 0.5rem);
  }
}

.action-cancel {
  padding: 0.6rem 1rem;
  border-radius: 6px;
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
.action-cancel:hover { border-color: #94A3B8; color: #0F172A; }
.action-confirm {
  flex: 1;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  border: none;
  background: #003D82;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}
.action-confirm:hover:not(:disabled) { background: #0055B8; }
.action-confirm:disabled { opacity: 0.5; cursor: not-allowed; }

/* Responsive adjustments for payment body and actions */
@media (max-width: 520px) {
  .payment-head { padding: 0.75rem 1rem; }
  .payment-title { font-size: 1rem; }
  .payment-subtitle { font-size: 0.8rem; }
  .payment-body { padding: 0.75rem 1rem; }
  .payment-body .field { margin-bottom: 0.6rem; }
  .payment-body .field label { font-size: 0.65rem; margin-bottom: 0.2rem; }
  .payment-body .input-wrap input { padding: 0.55rem 0.55rem 0.55rem 3rem; min-height: 40px; font-size: 0.85rem; }
  .operators { gap: 0.5rem; margin: 0.6rem 0; }
  .operator-card { padding: 0.5rem 0.3rem; min-height: 70px; border-radius: 6px; }
  .operator-card .v-image,
  .operator-card .v-img { height: 30px !important; }
  .operator-card span { font-size: 0.65rem; margin-top: 0.2rem; }
  .op-check { width: 16px; height: 16px; top: 3px; right: 3px; }
  .payment-summary { font-size: 0.8rem; padding: 0.6rem; margin-bottom: 0.6rem; }
  .ussd-block { margin-top: 0.3rem; }
  .ussd-hint { font-size: 0.75rem; margin-bottom: 0.3rem; }
  .ussd-row { padding: 0.5rem 0.6rem; margin-bottom: 0.3rem; }
  .ussd-code { font-size: 0.8rem; }
  .ussd-steps li { font-size: 0.75rem; padding: 0.15rem 0; padding-left: 0.9rem; }
  .action-cancel, .action-confirm { padding: 0.55rem 0.75rem; font-size: 0.8rem; }
  .field-error { font-size: 0.7rem; margin-top: 0.15rem; }
}

@media (max-width: 380px) {
  .payment-head { padding: 0.6rem 0.75rem; gap: 0.5rem; }
  .payment-title { font-size: 0.9rem; }
  .payment-subtitle { font-size: 0.7rem; }
  .payment-body { padding: 0.6rem 0.75rem; }
  .payment-body .field { margin-bottom: 0.5rem; }
  .payment-body .input-wrap input { padding: 0.5rem 0.5rem 0.5rem 2.8rem; min-height: 38px; font-size: 0.8rem; }
  .operators { gap: 0.4rem; margin: 0.5rem 0; }
  .operator-card { padding: 0.4rem 0.25rem; min-height: 62px; border-radius: 5px; }
  .operator-card .v-image,
  .operator-card .v-img { height: 26px !important; }
  .operator-card span { font-size: 0.6rem; margin-top: 0.15rem; }
  .op-check { width: 14px; height: 14px; top: 2px; right: 2px; }
  .payment-summary { font-size: 0.75rem; padding: 0.5rem; margin-bottom: 0.5rem; }
  .ussd-block { margin-top: 0.25rem; }
  .ussd-hint { font-size: 0.7rem; margin-bottom: 0.25rem; }
  .ussd-row { padding: 0.45rem 0.5rem; }
  .ussd-code { font-size: 0.75rem; }
  .ussd-steps li { font-size: 0.7rem; padding: 0.1rem 0; padding-left: 0.8rem; }
  .payment-actions { padding: 0.5rem 0; gap: 0.4rem; }
  .action-cancel, .action-confirm { padding: 0.5rem 0.6rem; font-size: 0.75rem; }
  .field-error { font-size: 0.65rem; }
}

@media (max-width: 320px) {
  .payment-head { padding: 0.5rem 0.6rem; }
  .payment-title { font-size: 0.85rem; }
  .payment-subtitle { font-size: 0.65rem; }
  .payment-body { padding: 0.5rem 0.6rem; }
  .payment-body .field { margin-bottom: 0.4rem; }
  .payment-body .input-wrap input { padding: 0.45rem 0.45rem 0.45rem 2.6rem; min-height: 36px; font-size: 0.75rem; }
  .operators { gap: 0.35rem; margin: 0.4rem 0; }
  .operator-card { padding: 0.35rem 0.2rem; min-height: 55px; }
  .operator-card .v-image,
  .operator-card .v-img { height: 22px !important; }
  .operator-card span { font-size: 0.55rem; }
  .op-check { width: 12px; height: 12px; top: 2px; right: 2px; }
  .payment-summary { font-size: 0.7rem; padding: 0.4rem; }
  .ussd-hint { font-size: 0.65rem; }
  .ussd-row { padding: 0.4rem 0.45rem; }
  .ussd-code { font-size: 0.7rem; }
  .ussd-steps li { font-size: 0.65rem; }
  .payment-actions { padding: 0.4rem 0; }
  .action-cancel, .action-confirm { padding: 0.45rem 0.5rem; font-size: 0.7rem; }
}

/* Expand transition */
.expand-enter-active, .expand-leave-active {
  transition: all 0.3s ease;
  max-height: 300px;
  opacity: 1;
  overflow: hidden;
}
.expand-enter, .expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* ═══════════════════════════════════════
   RESPONSIVE (General)
   ═══════════════════════════════════════ */
@media (max-width: 768px) {
  .enroll-main { padding: 1.25rem 0.75rem; gap: 1.5rem; }
  .form-card { padding: 1.5rem 1.1rem; border-radius: 10px; }
  .form-head h2 { font-size: 1.15rem; }
  .page-header { padding: 0.7rem 0.75rem; }
  .agent-ribbon { padding: 0.45rem 0.75rem; }
  .ribbon-inner { gap: 0.5rem; }
  .serial-pill { font-size: 0.7rem; padding: 0.15rem 0.45rem; }
  .type-btn { font-size: 0.75rem; padding: 0.5rem 0.3rem; gap: 0.25rem; }
  .type-btn .type-badge { font-size: 0.55rem; padding: 0.1rem 0.3rem; margin-left: 0.1rem; }
  .operators { gap: 0.5rem; }
  .payment-card { border-radius: 10px !important; }
  .payment-head { padding: 1rem; }
  .payment-body { padding: 1rem; }
  .operator-card { padding: 0.6rem 0.4rem; }
  .operator-card .v-image { height: 36px; }
  .operator-card span { font-size: 0.7rem; }
  .context-title { font-size: 1.8rem; }
  .input-wrap input { font-size: 0.85rem; min-height: 42px; }
}

@media (max-width: 480px) {
  .enroll-main { padding: 1rem 0.5rem; }
  .form-card { padding: 1.25rem 0.9rem; border-radius: 8px; }
  .type-toggle { padding: 0.25rem; }
  .type-toggle::before { width: calc(50% - 0.25rem); top: 0.25rem; bottom: 0.25rem; }
  .type-btn { flex-direction: column; gap: 0.15rem; padding: 0.4rem 0.2rem; font-size: 0.7rem; }
  .type-btn .type-badge { margin-left: 0; font-size: 0.5rem; padding: 0.08rem 0.25rem; }
  .back-link span { display: none; }
  .header-spacer { width: 40px; }
  .brand { font-size: 1rem; }
  .context-title { font-size: 1.5rem; }
  .context-list li { padding: 0.7rem 0.8rem; }
  .plate-code { font-size: 0.8rem; }
  .operator-card { padding: 0.5rem; }
  .operator-card span { font-size: 0.65rem; }
  .action-cancel, .action-confirm { padding: 0.5rem 0.75rem; font-size: 0.8rem; }
}

@media (max-width: 360px) {
  .form-card { padding: 1rem 0.75rem; }
  .input-wrap input { font-size: 0.8rem; min-height: 40px; padding: 0.5rem 0.4rem 0.5rem 0; }
  .input-wrap > .v-icon:first-child { width: 32px; }
  .field label { font-size: 0.65rem; }
  .submit-btn { padding: 0.7rem; font-size: 0.85rem; }
  .type-btn { font-size: 0.65rem; padding: 0.35rem 0.15rem; }
  .payment-head { padding: 0.75rem; }
  .payment-title { font-size: 1rem; }
  .ussd-code { font-size: 0.8rem; }
}

@media (min-width: 960px) {
  .enroll-main { padding-top: 3rem; }
  .form-card { padding: 2.25rem 2rem; }
}
</style>
